import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Card,
  Chip,
  CardContent,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import configServ from "../../services/config";
import { useSelector } from "react-redux";
import { Edit, RemoveRedEyeOutlined } from "@mui/icons-material";
import KuldeviPhotoPop from "./KuldeviPhotoPop";

const KuldeviAdditionTable = ({ handleEdit, refreshKey }) => {
  const [kuldeviList, setKuldeviList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const { company_id } = useSelector((state) => state.GlobalVariables);

  // Filters & sorting
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("title");
  const [sortField, setSortField] = useState("kd.id");
  const [sortOrder, setSortOrder] = useState("desc");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const headers = [
    { label: "S.No.", value: "kd.id" },
    { label: "Title", value: "kd.title" },
    { label: "Traveller", value: "about_kuldevi" },
    { label: "Kuldevi Name", value: "km.name" },
    { label: "Author Name", value: "rm.name" },
    { label: "Status", value: "kd.active" },
    { label: "Action", value: "action" },
  ];

  const headerCellStyle = {
    fontSize: 14,
    fontWeight: "bold",
    backgroundColor: "#0c79e6ff",
    color: "white",
    padding: 8,
    textAlign: "center",
    cursor: "pointer",
  };
  const cellStyle = { py: 0.5, textAlign: "center" };

  // ✅ Fetch data dynamically from backend
  const fetchKuldeviList = async () => {
    try {
      setLoading(true);
      const dataToSend = {
        company_id,
        page: page + 1,
        limit: rowsPerPage,
        search_field: searchType,
        search_value: searchTerm.trim(),
        sort_field: sortField,
        sort_order: sortOrder,
      };

      const result = await configServ.getKuldeviDetails(dataToSend);
      if (result?.status === true) {
        setKuldeviList(result.data || []);
        setTotal(result.total || 0);
      } else {
        setKuldeviList([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching Kuldevi details:", error);
      setKuldeviList([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // 🚀 Re-fetch whenever filter/sort/pagination changes
  useEffect(() => {
    if (company_id) fetchKuldeviList();
  }, [
    company_id,
    page,
    rowsPerPage,
    searchTerm,
    searchType,
    sortField,
    sortOrder,
    refreshKey,
  ]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 🔁 Sorting toggle on header click
  const handleSort = (field) => {
    if (field === "kd.active" || field === "kd.id") return; // ignore action column

    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Reset filters
  const handleReset = () => {
    setSearchTerm("");
    setSearchType("title");
    setSortField("kd.id");
    setSortOrder("desc");
    setPage(0);
  };

  const [openImagePop, setOpenImagePop] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  const handleOpenPopup = (productId) => {
    setSelectedProductId(productId);
    setOpenImagePop(true);
  };

  return (
    <>
      <KuldeviPhotoPop
        open={openImagePop}
        onClose={() => setOpenImagePop(false)}
        productId={selectedProductId}
        fetchImages={configServ.getKuldeviPhotos}
      />
      {/* 🔹 Filters Section */}
      <Box
        sx={{
          mt: 2,
          mb: 3,
          p: 2,
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          width: "100%",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle1" fontWeight="bold">
              Total: <span style={{ color: "#1976d2" }}>{total}</span>
            </Typography>
          </Grid>

          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Search By</InputLabel>
              <Select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
                label="Search By"
              >
                <MenuItem value="title">Title</MenuItem>
                <MenuItem value="kuldevi_name">Kuldevi Name</MenuItem>
                <MenuItem value="author_name">Author Name</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={3}>
            <TextField
              size="small"
              fullWidth
              label="Search"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => {
                setPage(0);
                setSearchTerm(e.target.value);
              }}
            />
          </Grid>

          {/* <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Order</InputLabel>
              <Select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                label="Order"
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Grid> */}

          <Grid item xs={12} sm={2}>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<RestartAltIcon />}
              onClick={handleReset}
              fullWidth
            >
              Reset
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* 🔹 Table Section */}
      <TableContainer
        component={Paper}
        sx={{ display: { xs: "none", md: "block" } }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headers.map((header) => {
                const isActive = sortField === header.value;
                return (
                  <TableCell
                    key={header.value}
                    style={headerCellStyle}
                    onClick={() => handleSort(header.value)}
                    sx={{
                      cursor: !["kd.active", "kd.id", "action"].includes(
                        header.value
                      )
                        ? "pointer"
                        : "default",
                    }}
                  >
                    {header.label}
                    {!["kd.active", "kd.id", "action"].includes(
                      header.value
                    ) && (
                      <span style={{ marginLeft: 4 }}>
                        {isActive ? (sortOrder === "asc" ? "▲" : "▼") : "⇅"}
                      </span>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={headers.length} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : kuldeviList.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={headers.length}
                  align="center"
                  sx={{ color: "red" }}
                >
                  <b>No Data Found</b>
                </TableCell>
              </TableRow>
            ) : (
              kuldeviList.map((row, rowIndex) => (
                <TableRow
                  key={row.id}
                  sx={{ "&:hover": { backgroundColor: "#f5faff" } }}
                >
                  {/* <TableCell sx={cellStyle}>{row.id}</TableCell> */}
                  <TableCell sx={cellStyle}>
                    {page * rowsPerPage + rowIndex + 1}
                  </TableCell>
                  <TableCell sx={cellStyle}>{row.title || "—"}</TableCell>
                  <TableCell
                    sx={{
                      ...cellStyle,
                      //   maxWidth: 350, // adjust as needed
                      display: "-webkit-box",
                      WebkitLineClamp: 2, // number of lines to show
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      wordBreak: "break-word",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: row.about_kuldevi || "—",
                    }}
                  />

                  <TableCell sx={cellStyle}>
                    {row.kuldevi_name || "—"}
                  </TableCell>
                  <TableCell sx={cellStyle}>{row.author_name || "—"}</TableCell>
                  {/* <TableCell sx={cellStyle}>
                    {row.active === 1 ? "Active" : "Inactive"}
                  </TableCell> */}
                  <TableCell>
                    <Chip
                      label={row.active ? "Active" : "Inactive"}
                      color={row.active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      ...cellStyle,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {/* 🗑️ Delete Button */}
                    {/* <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(row)}
                        >
                          <GridDeleteIcon fontSize="small" />
                        </IconButton> */}

                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleEdit(row)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>

                    <IconButton
                      sx={{ color: "green" }}
                      onClick={() => handleOpenPopup(row.id)}
                      size="small"
                    >
                      <RemoveRedEyeOutlined fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Backend Pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Mobile Card View */}
      <Box sx={{ display: { xs: "block", md: "none" }, mt: 2 }}>
        <Grid container spacing={2}>
          {loading ? (
            <Grid
              item
              xs={12}
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ py: 4 }}
            >
              <CircularProgress size={28} />
            </Grid>
          ) : kuldeviList.length === 0 ? (
            <Grid item xs={12}>
              <Typography align="center" color="red" sx={{ py: 3 }}>
                <b>No Data Found</b>
              </Typography>
            </Grid>
          ) : (
            kuldeviList.map((row) => (
              <Grid item xs={12} key={row.id}>
                <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {row.title || "—"}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: row.about_kuldevi || "—",
                      }}
                    />

                    <Typography variant="caption" display="block">
                      Kuldevi: {row.kuldevi_name || "—"}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Author: {row.author_name || "—"}
                    </Typography>
                    <Typography variant="caption" display="block">
                      Status:{" "}
                      <Chip
                        label={row.active ? "Active" : "Inactive"}
                        color={row.active ? "success" : "default"}
                        size="small"
                        // sx={{ mt: 2 }}
                      />
                    </Typography>

                    <Box mt={1} display="flex" gap={1}>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEdit(row)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        sx={{ color: "green" }}
                        size="small"
                        onClick={() => handleOpenPopup(row.id)}
                      >
                        <RemoveRedEyeOutlined fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        {/* Optional Pagination for Mobile */}
        {!loading && kuldeviList.length > 0 && (
          <Box mt={2}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default KuldeviAdditionTable;
